import { libraryFunctions } from '../lib/resources/price'
import { isSuperUser } from '../lib/supplier'
import { USER_TIER_DATA_LOCAL_STORAGE_KEY, LOCAL_STORAGE_SUPER_USER_KEY, USER_TIER_DATA } from '../utils/constants'
import { getCookie, getParsedInternalTrafficCookie } from '../utils/cookies'
import { sendDataToZapier } from '../utils/fetcher'
import { getLocalStorageUserData } from '../utils/localStorage'
import { useAuth } from './useAuth'

const { getTakeRatePercentage } = libraryFunctions

type SendDataToZapier = (
  formData: FormData,
  handleSuccess?: () => void,
  handleError?: () => void,
  suggestions?: boolean
) => Promise<void>

/**
 * Hook created as a layer to easily add data points to all Zapier requests globally
 * @returns Function to send data to Zapier endpoint
 */
const useZapier = (): SendDataToZapier => {
  const { consumptionTier, user, isImpersonated } = useAuth()

  return async (
    formData: FormData,
    handleSuccess: () => void = () => {},
    handleError: () => void = () => {},
    suggestions = false,
  ) => {
    // Tracking ids
    const trackingIdsData = localStorage.getItem('trackingIds')
    formData.append('trackingIds', trackingIdsData || '')

    // Organisation total spend
    formData.append(USER_TIER_DATA.ORG_TOTAL_SPEND, consumptionTier?.toString() || 'not available - guest user')

    // Consumption tier segment
    const tierData = localStorage.getItem(USER_TIER_DATA_LOCAL_STORAGE_KEY)
    const tierSegment = tierData ? JSON.parse(tierData).tierSegment : 'not available - guest user'
    formData.append(USER_TIER_DATA.CONSUMPTION_TIER_SEGMENT, tierSegment)

    // Consumption margin percentage
    formData.append(
      USER_TIER_DATA.CONSUMPTION_MARGIN_PERCENTAGE,
      consumptionTier !== null // Null check as 0 also returns falsy
        ? getTakeRatePercentage(consumptionTier || 0).toString()
        : 'not available - guest user',
    )

    // Internal user email and id
    const { encryptedEmail, email } = getParsedInternalTrafficCookie()
    if (encryptedEmail && email) {
      formData.append('internalUserEmail', email)
      formData.append('internalUserId', encryptedEmail)
    }

    // Cookie consent status
    const cookieConsentData = getCookie('hasGivenDroppeCookieConsent')
    if (cookieConsentData) {
      const [, cookieValue] = cookieConsentData
      formData.append('hasGivenDroppeCookieConsent', cookieValue)
    } else {
      formData.append('hasGivenDroppeCookieConsent', 'false')
    }

    // Superuser email
    if (user && (isSuperUser(user) || isImpersonated)) {
      const superuserEmail = getLocalStorageUserData(LOCAL_STORAGE_SUPER_USER_KEY)?.email
        || user.email
      formData.append('superuserEmail', superuserEmail)
    }

    // Customer type
    const [,customerType] = getCookie('customerType') || ['', 'undefined']
    formData.append('customerType', customerType)

    await sendDataToZapier(formData, handleSuccess, handleError, suggestions)
  }
}

export default useZapier
